import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, StyledEngineProvider, Snackbar, Button, Alert } from '@mui/material';

import AppRoutes from './routes/AppRoutes';
import NavigationScroll from './layout/NavigationScroll';
import Theme from './themes';
import { NavigationBlockerProvider } from './components/NavigationBlockerProvider';
import { ButtonStyles } from './utils/general';
import { useTheme } from "@mui/styles";

//-----------------------|| APP ||-----------------------//

const App = ({ newVersionAvailable }) => {
    const Customization = useSelector((state) => state.customization);
    const [showUpdateBanner, setShowUpdateBanner] = useState(newVersionAvailable);
    const [showSessionWarning, setShowSessionWarning] = useState(false);
    const [timeLeft, setTimeLeft] = useState(null);
    const theme = useTheme();

    /**
 * useEffect (show update banner on new version detection)
 *
 * @summary Periodically checks for the new version flag in localStorage and displays the update banner if set.
 *
 * @description
 * Sets up a 5-second interval to check if 'new_version_detected' is set to 'true' in localStorage. If detected, updates the state to show the update banner. Cleans up the interval on component unmount.
 *
 * @returns {void}
 *
 * @example
 * useEffect(() => {
 *   // show update banner on new version detection
 * }, []);
 */
    useEffect(() => {
        const checkInterval = setInterval(() => {
            if (localStorage.getItem('new_version_detected') === 'true') {
                setShowUpdateBanner(true);
            }
        }, 5000);
        return () => clearInterval(checkInterval);
    }, []);

    /**
 * handleRefresh
 *
 * @summary Clears localStorage and sessionStorage, then reloads the page to update the app.
 *
 * @description
 * Removes all data from localStorage and sessionStorage, then reloads the current page to ensure the latest app version is loaded.
 *
 * @returns {void}
 *
 * @example
 * handleRefresh();
 */
    const handleRefresh = () => {
        localStorage.clear();
        sessionStorage.clear();
        window.location.reload(true);
    };

    /**
 * handleSessionLogout
 *
 * @summary Logs out the user by clearing authentication tokens and session data, then redirects to the login page.
 *
 * @description
 * Removes authentication tokens and token expiry from localStorage, clears sessionStorage, and navigates the user to the login page to complete the logout process.
 *
 * @returns {void}
 *
 * @example
 * handleSessionLogout();
 */
    const handleSessionLogout = useCallback(() => {
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("tokenExpiry");
        sessionStorage.clear();
        window.location.href = "/login";
    }, []);

    /**
 * useEffect (session expiry countdown and auto-logout)
 *
 * @summary Tracks session expiry and automatically logs out the user when the token expires.
 *
 * @description
 * Sets up a 1-second interval to check the remaining time until the authentication token expires. Updates the countdown timer state and shows a session warning if less than 1 hour remains. If the token has expired, triggers the logout process and clears the interval. Cleans up the interval on component unmount or dependency change.
 *
 * @returns {void}
 *
 * @example
 * useEffect(() => {
 *   // session expiry countdown and auto-logout
 * }, [handleSessionLogout, showSessionWarning]);
 */
    useEffect(() => {
        const expiry = localStorage.getItem("tokenExpiry");
        if (!expiry) return;

        const interval = setInterval(() => {
            const remaining = Number(expiry) - Date.now();

            if (remaining <= 0) {
                handleSessionLogout();
                clearInterval(interval);
                return;
            }

            setTimeLeft(remaining);

            if (remaining <= 60 * 60 * 1000 && !showSessionWarning) {
                setShowSessionWarning(true);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [handleSessionLogout, showSessionWarning]);

    /**
 * formatTime
 *
 * @summary Formats a time duration in milliseconds into a human-readable string (h m s).
 *
 * @description
 * Converts the provided time in milliseconds to hours, minutes, and seconds, and returns a formatted string. Returns "Loading..." if the input is falsy.
 *
 * @param {number} ms - The time duration in milliseconds.
 *
 * @returns {string} The formatted time string in the format "{h}h {m}m {s}s".
 *
 * @example
 * const formatted = formatTime(3661000); // "1h 1m 1s"
 */
    const formatTime = (ms) => {
        if (!ms) return "Loading...";
        const totalSec = Math.floor(ms / 1000);
        const hrs = Math.floor(totalSec / 3600);
        const mins = Math.floor((totalSec % 3600) / 60);
        const secs = totalSec % 60;
        return `${hrs}h ${mins}m ${secs}s`;
    };

    return (
        <NavigationBlockerProvider>
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={Theme(Customization)}>
                    <CssBaseline />
                    <Snackbar
                        open={showUpdateBanner}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        <Alert
                            severity="info"
                            sx={{ width: '100%', backgroundColor: '#fff', color: '#1976d2' }}
                            action={
                                <Button
                                    size="small"
                                    onClick={handleRefresh}
                                    style={ButtonStyles(theme)}
                                >
                                    Refresh
                                </Button>
                            }
                        >
                            A new version of the app is available.
                        </Alert>
                    </Snackbar>

                    <Snackbar
                        open={showSessionWarning}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        <Alert
                            severity="warning"
                            sx={{ width: '100%', backgroundColor: '#fff3cd', color: '#856404' }}
                            action={
                                <Button
                                    size="small"
                                    onClick={() => setShowSessionWarning(false)}
                                    style={ButtonStyles(theme)}
                                >
                                    Hide
                                </Button>
                            }
                        >
                            ⚠️ Your session is about to expire! Time Left: <strong>{formatTime(timeLeft)}</strong> <br/><br/>
                                Please logout and login again, or it will automatically logout.
                        </Alert>
                    </Snackbar>
                    <NavigationScroll>
                        <AppRoutes />
                    </NavigationScroll>
                </ThemeProvider>
            </StyledEngineProvider>
        </NavigationBlockerProvider>
    );
};

export default App;